/**
 * Drug Programs Page
 * 
 * Dashboard view of all drug programs with filtering and pharmacy relationships
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { drugProgramAPI, apiHelpers, getProductImageUrl } from '../services/api'
import api from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { canSeeDrugPrices } from '../lib/drugPricing'
import useDraftStore from '../stores/draftStore'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Badge
} from '../components/ui'
import DrugProgramEditModal from '../components/DrugProgramEditModal'
import ImportProgramsModal from '../components/ImportProgramsModal'
import AddPharmacyToProgramModal from '../components/AddPharmacyToProgramModal'

export default function DrugProgramsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)
  const [tags, setTags] = useState([])
  const [pagination, setPagination] = useState({})
  const [editModal, setEditModal] = useState({ isOpen: false, program: null })
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)

  // Get auth state to check clinic selection
  const { user, currentClinicId } = useAuthStore()
  const showPrices = canSeeDrugPrices(user)
  const { addChange, getChangesByType } = useDraftStore()

  // Roles that can edit configs (excludes viewer and data_entry)
  const canEditConfigs = user && ['super_admin', 'clinic_admin', 'program_manager'].includes(user.role)

  // Helper function to merge drafts with database programs
  const mergeProgramsWithDrafts = (dbPrograms) => {
    const draftChanges = getChangesByType('drug_program')
    
    console.log('[MERGE] Database programs:', dbPrograms.length)
    console.log('[MERGE] Draft changes:', draftChanges.length)
    
    // Start with database programs
    let mergedPrograms = [...dbPrograms]
    
    // Apply draft changes
    draftChanges.forEach(change => {
      if (change.operation === 'create') {
        // Add new programs from drafts with a draft indicator
        mergedPrograms.push({
          ...change.new_data,
          _isDraft: true,
          _draftChangeId: change.id
        })
      } else if (change.operation === 'update') {
        // Update existing programs or drafts
        const index = mergedPrograms.findIndex(p => 
          p.entity_id === change.resource_id || p._id === change.resource_id
        )
        if (index !== -1) {
          mergedPrograms[index] = {
            ...mergedPrograms[index],
            ...change.new_data,
            _isDraft: true,
            _draftChangeId: change.id
          }
        }
      } else if (change.operation === 'delete') {
        // Remove deleted programs
        mergedPrograms = mergedPrograms.filter(p => 
          p.entity_id !== change.resource_id && p._id !== change.resource_id
        )
      }
    })
    
    console.log('[MERGE] Merged programs:', mergedPrograms.length)
    
    return mergedPrograms
  }

  // Search and filter state
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [tagFilter, setTagFilter] = useState(searchParams.get('tag') || 'all')
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all')
  const [deaFilter, setDeaFilter] = useState(searchParams.get('dea_required') || 'all')
  const [localPharmacyFilter, setLocalPharmacyFilter] = useState(searchParams.get('local_pharmacy_only') || 'all')
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'drug_name')
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort_order') || 'asc')
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
  
  // Modal state for program details
  const [selectedProgram, setSelectedProgram] = useState(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isPharmaciesModalOpen, setIsPharmaciesModalOpen] = useState(false)
  const [programPharmacies, setProgramPharmacies] = useState([])
  const [loadingPharmacies, setLoadingPharmacies] = useState(false)
  const [isAddPharmacyModalOpen, setIsAddPharmacyModalOpen] = useState(false)

  // Check if user has access - super admins can access even without clinic selection
  const hasClinicAccess = user?.role === 'super_admin' || (currentClinicId && user?.clinicIds?.includes(currentClinicId))

  // Fetch drug programs
  const fetchPrograms = async () => {
    if (!hasClinicAccess) {
      setError('Please select a clinic to view drug program data')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const params = {
        search: search || undefined,
        tag: tagFilter !== 'all' ? tagFilter : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        dea_required: deaFilter !== 'all' ? deaFilter : undefined,
        local_pharmacy_only: localPharmacyFilter !== 'all' ? localPharmacyFilter : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage,
        limit: 20,
        include_inactive: 'false'
      }

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key])

      const response = await drugProgramAPI.list(params)
      
      console.log('[DRUG PROGRAMS PAGE] Fetch response:', response.data)
      
      if (response.data.success) {
        // Backend now returns 'programs' instead of 'data'
        const programsList = response.data.programs || response.data.data || []
        
        // Merge with draft changes
        const mergedPrograms = mergeProgramsWithDrafts(programsList)
        setPrograms(mergedPrograms)
        setPagination(response.data.pagination)
        
        console.log('[DRUG PROGRAMS PAGE] Set programs (with drafts):', mergedPrograms.length)
      } else {
        throw new Error('Failed to fetch drug programs')
      }
    } catch (err) {
      console.error('Failed to fetch drug programs:', err)
      setError(apiHelpers.handleError(err).message)
    } finally {
      setLoading(false)
    }
  }

  // Fetch statistics
  const fetchStats = async () => {
    if (!hasClinicAccess) return

    try {
      const response = await drugProgramAPI.getStats()
      if (response.data.success) {
        setStats(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch drug program stats:', err)
    }
  }

  // Fetch available tags
  const fetchTags = async () => {
    if (!hasClinicAccess) return

    try {
      const response = await drugProgramAPI.getTags()
      if (response.data.success) {
        setTags(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err)
    }
  }

  // Modal handlers
  const handleEditProgram = (program) => {
    setEditModal({ isOpen: true, program })
  }

  const closeEditModal = () => {
    setEditModal({ isOpen: false, program: null })
  }

  const handleProgramSave = (updatedProgram) => {
    console.log('Drug program saved:', updatedProgram)
    // TODO: Update the programs list with the updated program
    // TODO: Refresh data from backend
    closeEditModal()
  }

  // Load pharmacies for a program
  const loadProgramPharmacies = async (programId) => {
    try {
      setLoadingPharmacies(true)
      const response = await api.get(`/drug-programs/${programId}/pharmacies`)
      setProgramPharmacies(response.data.data || [])
      setIsPharmaciesModalOpen(true)
    } catch (error) {
      console.error('Error loading program pharmacies:', error)
      alert('Failed to load pharmacies for this program')
    } finally {
      setLoadingPharmacies(false)
    }
  }

  // Toggle program active status
  const handleToggleProgramStatus = (program) => {
    const newStatus = !program.is_active
    const statusText = newStatus ? 'enable' : 'disable'
    
    if (!confirm(`Are you sure you want to ${statusText} "${program.drug_name}"?`)) {
      return
    }

    try {
      const updatedProgram = {
        ...program,
        is_active: newStatus
      }

      // Add change to draft store
      const action = newStatus ? 'Enable' : 'Disable'
      const details = []
      if (program.entity_id) details.push(program.entity_id)
      if (program.pharmacies?.length) details.push(`${program.pharmacies.length} pharmacies`)
      
      addChange({
        resource_type: 'drug_program',
        resource_id: program.entity_id || program._id,
        operation: 'update',
        original_data: program,
        new_data: updatedProgram,
        description: `${action}: ${program.drug_name} (${details.join(', ')})`
      })

      console.log(`[PROGRAM] ${newStatus ? 'Enabled' : 'Disabled'} program in drafts`)
      
      // Close detail modal if open
      setIsDetailModalOpen(false)
      
      // Show success message
      alert(`Program "${program.drug_name}" will be ${newStatus ? 'enabled' : 'disabled'} when you publish your changes.`)
    } catch (error) {
      console.error('Error toggling program status:', error)
      alert('Failed to toggle program status')
    }
  }

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (tagFilter !== 'all') params.set('tag', tagFilter)
    if (typeFilter !== 'all') params.set('type', typeFilter)
    if (deaFilter !== 'all') params.set('dea_required', deaFilter)
    if (localPharmacyFilter !== 'all') params.set('local_pharmacy_only', localPharmacyFilter)
    if (sortBy !== 'drug_name') params.set('sort_by', sortBy)
    if (sortOrder !== 'asc') params.set('sort_order', sortOrder)
    if (currentPage > 1) params.set('page', currentPage.toString())

    setSearchParams(params)
  }, [search, tagFilter, typeFilter, deaFilter, localPharmacyFilter, sortBy, sortOrder, currentPage, setSearchParams])

  // Fetch data when filters change
  useEffect(() => {
    fetchPrograms()
  }, [search, tagFilter, typeFilter, deaFilter, localPharmacyFilter, sortBy, sortOrder, currentPage, currentClinicId])

  // Fetch stats and tags on mount
  useEffect(() => {
    fetchStats()
    fetchTags()
  }, [currentClinicId])

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearch(value)
    setCurrentPage(1) // Reset to first page when searching
  }

  // Get program type badge variant
  const getTypeVariant = (program) => {
    if (program.dea) return 'destructive'
    if (program.local_pharmacy) return 'secondary'
    return 'default'
  }

  // Format price display
  const formatPrice = (price) => {
    if (!price) return 'N/A'
    return `$${(price / 100).toFixed(2)}`
  }

  // Handle program detail view
  const handleViewDetails = (program) => {
    setSelectedProgram(program)
    setIsDetailModalOpen(true)
  }

  if (!hasClinicAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>Please select a clinic from the header dropdown to view drug programs.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Drug Programs</h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
                Manage drug programs and pharmacy configurations
              </p>
            </div>
            {canEditConfigs && (
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  onClick={() => setIsImportModalOpen(true)}
                  className="text-sm"
                >
                  Import
                </Button>
                <Button
                  onClick={() => setEditModal({ isOpen: true, program: null })}
                  className="text-sm"
                >
                  Add Program
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Active</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.overview?.total_active || 0}</div>
                <p className="text-xs text-muted-foreground">
                  +{stats.overview?.total_inactive || 0} inactive
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">DEA Required</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.overview?.dea_required || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.overview?.local_pharmacy_only || 0} local pharmacy only
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Compounds</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.by_type?.compound || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {(stats.by_type?.brand || 0) + (stats.by_type?.generic || 0)} brand/generic
                </p>
              </CardContent>
            </Card>

            {/* Drug costs are what the pharmacy charges us, and are withheld
                from the clinic-facing roles. The API strips them too — see
                backend/src/utils/drugPricing.js. */}
            {showPrices && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Ingredient Price</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary">
                    {formatPrice(stats.price_analysis?.avg_price)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {stats.price_analysis?.total_price_points || 0} drug configurations
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="container mx-auto px-4 pb-6">
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search programs..."
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tag-filter">Tag</Label>
                <Select value={tagFilter} onValueChange={setTagFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All tags" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tags</SelectItem>
                    {tags.slice(0, 10).map(tag => (
                      <SelectItem key={tag.name} value={tag.name}>
                        {tag.name} ({tag.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="type-filter">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="compound">Compound</SelectItem>
                    <SelectItem value="brand">Brand</SelectItem>
                    <SelectItem value="generic">Generic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dea-filter">DEA Required</Label>
                <Select value={deaFilter} onValueChange={setDeaFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    <SelectItem value="true">DEA Required</SelectItem>
                    <SelectItem value="false">No DEA Required</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="local-filter">Local Pharmacy</Label>
                <Select value={localPharmacyFilter} onValueChange={setLocalPharmacyFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    <SelectItem value="true">Local Only</SelectItem>
                    <SelectItem value="false">Network Available</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sort">Sort By</Label>
                <Select value={`${sortBy}:${sortOrder}`} onValueChange={(value) => {
                  const [field, order] = value.split(':')
                  setSortBy(field)
                  setSortOrder(order)
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drug_name:asc">Name (A-Z)</SelectItem>
                    <SelectItem value="drug_name:desc">Name (Z-A)</SelectItem>
                    <SelectItem value="pharmacy_count:desc">Most Pharmacies</SelectItem>
                    {/* Ordering by price reveals the ranking even with the
                        numbers withheld, so these go too. */}
                    {showPrices && <SelectItem value="average_ingredient_price:asc">Lowest Price</SelectItem>}
                    {showPrices && <SelectItem value="average_ingredient_price:desc">Highest Price</SelectItem>}
                    <SelectItem value="created_at:desc">Newest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8">
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-pulse text-muted-foreground">Loading drug programs...</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {error && (
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8">
              <div className="text-center text-destructive">
                <p>Error loading drug programs: {error}</p>
                <Button 
                  variant="outline" 
                  onClick={fetchPrograms}
                  className="mt-4"
                >
                  Try Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Programs List */}
      {!loading && !error && programs && programs.length > 0 && (
        <div className="container mx-auto px-4 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            {programs.map((program) => (
              <Card 
                key={program._id || program.entity_id || program._draftChangeId} 
                className={`hover:shadow-md transition-shadow ${program._isDraft ? 'border-amber-500 border-2' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="space-y-2">
                    {/* Badges row - always at top */}
                    <div className="flex flex-wrap gap-1.5 min-h-[24px]">
                      {program._isDraft && (
                        <Badge className="bg-amber-500 text-white text-xs">DRAFT</Badge>
                      )}
                      {program.dea && <Badge variant="destructive" className="text-xs">DEA</Badge>}
                      {program.local_pharmacy && <Badge variant="secondary" className="text-xs">Local</Badge>}
                      {program.has_fill_schedule && <Badge variant="outline" className="text-xs">Phased</Badge>}
                    </div>
                    {/* Title and description with optional product image */}
                    <div className="flex items-start gap-3">
                      {getProductImageUrl(program.dosage?.img) && (
                        <img
                          src={getProductImageUrl(program.dosage?.img)}
                          alt=""
                          className="w-10 h-10 rounded object-contain bg-muted flex-shrink-0"
                          onError={(e) => { e.target.style.display = 'none' }}
                        />
                      )}
                      <div className="min-w-0">
                        <CardTitle className="text-lg leading-tight">{program.drug_name}</CardTitle>
                        <CardDescription className="mt-1 text-xs">
                          {program.entity_id} • {program.dosespot_type}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {showPrices && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Ingredient Price:</span>
                        <span className="font-semibold text-primary">{formatPrice(program.average_ingredient_price)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Pharmacies:</span>
                      <span>{program.pharmacy_count || 0}</span>
                    </div>
                    {program.tags && program.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {program.tags.slice(0, 3).map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {program.tags.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{program.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/drug-programs/${program._id}`)}
                      className="flex-1"
                    >
                      View Details
                    </Button>
                    {canEditConfigs && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditProgram(program)}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button 
                variant="outline" 
                disabled={!pagination.has_prev}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="px-4 py-2 text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button 
                variant="outline" 
                disabled={!pagination.has_next}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && (!programs || programs.length === 0) && (
        <div className="container mx-auto px-4 pb-8">
          <Card>
            <CardContent className="p-8">
              <div className="text-center">
                <p className="text-muted-foreground mb-4">No drug programs found matching your criteria.</p>
                <Button variant="outline" onClick={() => {
                  setSearch('')
                  setTagFilter('all')
                  setTypeFilter('all')
                    setDeaFilter('all')
                    setLocalPharmacyFilter('all')
                    setCurrentPage(1)
                  }}>
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {/* Program Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedProgram?.drug_name}</DialogTitle>
            <DialogDescription>
              Drug program details and pharmacy configurations
            </DialogDescription>
          </DialogHeader>
          
          {selectedProgram && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Program Information</h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Entity ID:</span> {selectedProgram.entity_id}</p>
                    <p><span className="text-muted-foreground">Type:</span> {selectedProgram.dosespot_type}</p>
                    <p><span className="text-muted-foreground">DEA Required:</span> {selectedProgram.dea ? 'Yes' : 'No'}</p>
                    <p><span className="text-muted-foreground">Local Pharmacy:</span> {selectedProgram.local_pharmacy ? 'Yes' : 'No'}</p>
                    <p><span className="text-muted-foreground">Sync Required:</span> {selectedProgram.sync_required ? 'Yes' : 'No'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Pricing & Availability</h4>
                  <div className="space-y-1 text-sm">
                    {showPrices && <p><span className="text-muted-foreground">Ingredient Price:</span> <span className="font-semibold text-primary">{formatPrice(selectedProgram.average_ingredient_price)}</span></p>}
                    <p><span className="text-muted-foreground">Pharmacies:</span> {selectedProgram.pharmacy_count}</p>
                    <p><span className="text-muted-foreground">Fill Schedule:</span> {selectedProgram.has_fill_schedule ? 'Phased Program' : 'Single Fill'}</p>
                  </div>
                </div>
              </div>

              {selectedProgram.dosage && (
                <div>
                  <h4 className="font-medium mb-2">Dosage Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedProgram.dosage.dosage && (
                      <p><span className="text-muted-foreground">Dosage:</span> {selectedProgram.dosage.dosage}</p>
                    )}
                    {selectedProgram.dosage.type && (
                      <p><span className="text-muted-foreground">Type:</span> {selectedProgram.dosage.type}</p>
                    )}
                    {selectedProgram.dosage.period && (
                      <p><span className="text-muted-foreground">Period:</span> {selectedProgram.dosage.period}</p>
                    )}
                    {selectedProgram.dosage.description && (
                      <p className="col-span-2"><span className="text-muted-foreground">Description:</span> {selectedProgram.dosage.description}</p>
                    )}
                  </div>
                </div>
              )}

              {selectedProgram.tags && selectedProgram.tags.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProgram.tags.map((tag, index) => (
                      <Badge key={index} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={() => loadProgramPharmacies(selectedProgram._id)}
                  disabled={loadingPharmacies}
                >
                  {loadingPharmacies ? 'Loading...' : 'View Pharmacies'}
                </Button>
{canEditConfigs && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditModal({ isOpen: true, program: selectedProgram })
                        setIsDetailModalOpen(false)
                      }}
                    >
                      Edit Program
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddPharmacyModalOpen(true)
                        setIsDetailModalOpen(false)
                      }}
                    >
                      Add Pharmacy
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleToggleProgramStatus(selectedProgram)}
                    >
                      {selectedProgram?.is_active ? 'Disable' : 'Enable'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Drug Program Modal */}
      <DrugProgramEditModal
        program={editModal.program}
        isOpen={editModal.isOpen}
        onClose={closeEditModal}
        onSave={handleProgramSave}
      />

      {/* Import Programs Modal */}
      <ImportProgramsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        currentClinicId={currentClinicId}
        onImport={() => {
          // Reload programs list after import
          fetchPrograms()
        }}
      />

      {/* Program Pharmacies Modal */}
      <Dialog open={isPharmaciesModalOpen} onOpenChange={setIsPharmaciesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Pharmacies for {selectedProgram?.drug_name}</DialogTitle>
            <DialogDescription>
              Pharmacies configured to dispense this medication
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {programPharmacies.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No pharmacies configured for this program
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Pharmacy Name</th>
                      <th className="text-left p-3 font-medium">Gateway</th>
                      <th className="text-left p-3 font-medium">States</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programPharmacies.map((pharmacy) => (
                      <tr key={pharmacy._id} className="border-t hover:bg-muted/30">
                        <td className="p-3 font-medium">{pharmacy.pharmacy_name}</td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {pharmacy.pharmacy_gateway || 'N/A'}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {pharmacy.states?.length || 0} states
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pharmacy.pharmacy_config?.enable_pharmacy ? 
                            'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100' : 
                            'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                          }`}>
                            {pharmacy.pharmacy_config?.enable_pharmacy ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-3">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              window.location.href = `/pharmacy?id=${pharmacy._id}`
                            }}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setIsPharmaciesModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Pharmacy to Program Modal */}
      <AddPharmacyToProgramModal
        isOpen={isAddPharmacyModalOpen}
        onClose={() => setIsAddPharmacyModalOpen(false)}
        program={selectedProgram}
        onSave={() => {
          // Reload program details (this called an undefined loadPrograms)
          fetchPrograms()
        }}
      />
    </div>
  )
}
